import React from 'react';

/**
 * App-wide error boundary. Any uncaught render exception in a child would
 * previously crash the whole route to a blank white screen (React removes the
 * DOM subtree). Trainers reported this after adding a client — even though
 * the add succeeded on the backend, the follow-up render exploded and left
 * a white page with no way back. This wrapper renders a friendly fallback
 * with a "Reload" button and logs the details to the console for debugging.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught render error:', error, info);
  }

  reset = () => {
    this.setState({ error: null });
    try { window.location.reload(); } catch { /* noop */ }
  };

  render() {
    if (!this.state.error) return this.props.children;
    const language = (typeof document !== 'undefined' && document.documentElement.lang) || 'de';
    const isDe = language.startsWith('de');
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-lg p-8 text-center" data-testid="error-boundary-fallback">
          <div className="text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">
            {isDe ? 'Etwas ist schiefgelaufen' : 'Something went wrong'}
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            {isDe
              ? 'Bitte lade die Seite neu. Wenn das Problem weiterhin besteht, melde dich beim Admin.'
              : 'Please reload the page. If this keeps happening, contact your admin.'}
          </p>
          <button
            onClick={this.reset}
            className="bg-emerald-500 text-white font-bold px-6 py-2.5 rounded-lg hover:bg-emerald-600 transition"
            data-testid="error-boundary-reload"
          >
            {isDe ? 'Seite neu laden' : 'Reload page'}
          </button>
          {this.state.error?.message && (
            <details className="mt-6 text-left text-[10px] text-slate-400">
              <summary className="cursor-pointer">Details</summary>
              <pre className="whitespace-pre-wrap break-all mt-2">
                {String(this.state.error.message)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
